import React, { useEffect, useState } from 'react';
import { Col, Row, Select, Table, Tooltip } from 'antd';
import { api, endpoints } from '../../api';
import {
  AcceptRepresentativeSvg,
  BlockRepresentativeSvg,
  ImportantRepresentativeSvg,
  RefuseRepresentativeSvg,
} from '../../icons';
import {
  BlueLayout,
  CustomButton,
  CustomSelect,
  CustomTableContainer,
  FormBlock,
  FormContainer,
  FormInput,
  PagesContainerUI,
  SvgFormer,
} from '../../styles/styled';
import { sortPublishers, sortStatus } from '../../utils/sortObject';
import { PaginationType } from '../ExpertPage/ExpertPage';
import { ExpertisesStatusType, PublishersType } from '../NewApplicationPage/types';
import StatusPopup from './components/StatusPopup';
import {
  RepresentativeSearchType,
  RepresentativesResponseType,
  RepresentativesType,
  RepresentativeType,
} from './types';

const { Option } = Select;

export const RepresentativePage = () => {
  const [representativesState, setRepresentatives] = useState<RepresentativesType>([]);
  const [paginationState, setPaginationState] = useState<PaginationType>({ pagination: 1, perPage: 10, count: 0 });
  const [publishersState, setPublishers] = useState<PublishersType>([]);
  const [statusesState, setStatuses] = useState<ExpertisesStatusType>([]);
  const [searchState, setSearchState] = useState<RepresentativeSearchType>({});
  const [statusPopupState, setStatusPopup] = useState<RepresentativeType | null>();
  const [searchStatus, setSearchStatus] = useState<'search' | 'clear' | 'neutral'>('clear');

  const StatusButtonRenderer = ({ representative }: { representative: RepresentativeType }) => {
    switch (representative.status.code) {
      case 'not_confirmed':
        return (
          <Tooltip placement="left" title="Не подтвержден">
            <SvgFormer>
              <ImportantRepresentativeSvg />
              <CustomButton onClick={() => setStatusPopup(representative)} lined>
                Подтвердить / отклонить
              </CustomButton>
            </SvgFormer>
          </Tooltip>
        );
      case 'confirmed':
        return (
          <Tooltip placement="left" title="Подтвержден">
            <SvgFormer>
              <AcceptRepresentativeSvg />
              <CustomButton onClick={() => setStatusPopup(representative)} lined>
                Заблокировать
              </CustomButton>
            </SvgFormer>
          </Tooltip>
        );
      case 'refused':
        return (
          <Tooltip placement="left" title="Отклонен">
            <SvgFormer>
              <RefuseRepresentativeSvg />
              <CustomButton onClick={() => setStatusPopup(representative)} lined>
                заблокировать
              </CustomButton>
            </SvgFormer>
          </Tooltip>
        );
      case 'blocked':
        return (
          <Tooltip placement="left" title="Заблокирован">
            <SvgFormer>
              <BlockRepresentativeSvg />
              <CustomButton onClick={() => setStatusPopup(representative)} lined>
                Разблокировать
              </CustomButton>
            </SvgFormer>
          </Tooltip>
        );
      default:
        return <p>{representative?.status?.code}</p>;
    }
  };

  useEffect(() => {
    (async () => {
      try {
        if (searchStatus === 'clear') {
          const representativesResponse = await api.get<RepresentativesResponseType>(
            endpoints.getRepresentatives({ page: 0, perPage: 50000 }),
          );

          setPaginationState((prevState) => {
            return { ...prevState, count: representativesResponse?.data?.count };
          });

          setRepresentatives(
            representativesResponse?.data?.representatives?.map((el, index) => {
              return { ...el, key: index };
            }),
          );
        } else if (searchStatus === 'search') {
          const params = {
            ...searchState,
            page: 0,
            perPage: 50000,
          };

          const representativesResponse = await api.get<RepresentativesResponseType>(
            endpoints.getRepresentatives(params),
          );

          setPaginationState((prevState) => {
            return { ...prevState, pagination: 1, count: representativesResponse?.data?.representatives?.length };
          });

          setSearchStatus('neutral');

          setRepresentatives(
            representativesResponse?.data?.representatives?.map((el, index) => {
              return { ...el, key: index };
            }),
          );
        }
      } catch (err) {}
    })();
    // eslint-disable-next-line
  }, [searchStatus, paginationState.pagination, statusPopupState]);

  useEffect(() => {
    (async () => {
      try {
        const publishersResponse = await api.get<PublishersType>(endpoints.getPublishers(0, 1000));
        const expertiseStatuses = await api.get<ExpertisesStatusType>(endpoints.representativeStatus());

        setStatuses(expertiseStatuses.data.sort(sortStatus));
        setPublishers(publishersResponse.data.sort(sortPublishers));
      } catch (err) {}
    })();
  }, []);

  const RepresentativeTableColumns = [
    {
      title: '№ п/п',
      dataIndex: 'id',
      key: 'id',
      render: (value: string, book: RepresentativeType) => <p>{book?.key + 1}</p>,
    },
    {
      title: 'Наименование издательства',
      dataIndex: 'izdName',
      key: 'izdName',
      render: (value: string, representative: RepresentativeType) => <p>{representative?.publisher?.title}</p>,
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
      render: (value: string, representative: RepresentativeType) => (
        <p>{representative?.last_name + ' ' + representative?.first_name + ' ' + representative?.patronymic}</p>
      ),
    },
    {
      title: 'Контакты',
      dataIndex: 'contacts',
      key: 'contacts',
      render: (value: string, representative: RepresentativeType) => (
        <p>
          {representative?.phone}
          <br />
          {representative?.email}
        </p>
      ),
    },
    {
      title: 'Подтверждение полномочий',
      dataIndex: 'authConfirmation',
      key: 'authConfirmation',
      render: (value: string, representative: RepresentativeType) => {
        return (
          <a
            style={{ textDecoration: 'underline' }}
            href={representative?.file?.file_path}
            target="_blank"
            rel="noreferrer"
          >
            Посмотреть
          </a>
        );
      },
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (value: string, representative: RepresentativeType) => (
        <StatusButtonRenderer representative={representative} />
      ),
    },
  ];

  return (
    <PagesContainerUI>
      <BlueLayout>ПРЕДСТАВИТЕЛИ ИЗДАТЕЛЬСТВ</BlueLayout>
      {statusPopupState && (
        <StatusPopup
          statusesState={statusesState}
          representative={statusPopupState}
          closePopup={() => setStatusPopup(null)}
        />
      )}
      <FormBlock>
        <FormContainer>
          <Row gutter={[15, 20]}>
            <Col span={24}>
              <div className="text">Фамилия</div>
              <FormInput
                value={searchState?.last_name}
                onChange={(e) => setSearchState({ ...searchState, last_name: e.target.value })}
              />
            </Col>
            <Col span={12}>
              <div className="text">Имя</div>
              <FormInput
                value={searchState?.first_name}
                onChange={(e) => setSearchState({ ...searchState, first_name: e.target.value })}
              />
            </Col>
            <Col span={12}>
              <div className="text">Отчество</div>
              <FormInput
                value={searchState?.patronymic}
                onChange={(e) => setSearchState({ ...searchState, patronymic: e.target.value })}
              />
            </Col>
            <Col span={24}>
              <div className="text">Наименование издательства</div>
              <CustomSelect
                onChange={(value, option) => {
                  setSearchState({
                    ...searchState,
                    publisher: parseInt(Object.values(option)[0]),
                  });
                }}
                value={
                  searchState?.publisher
                    ? publishersState?.find((el) => el.id === searchState.publisher)?.id
                    : undefined
                }
                showSearch
              >
                <Option key={null} value={undefined}>
                  Все
                </Option>
                {publishersState?.map((el) => {
                  return (
                    <Option key={el.id} value={el.id}>
                      {el.title}
                    </Option>
                  );
                })}
              </CustomSelect>
            </Col>
            <Col span={24}>
              <div className="text">Статус</div>
              <CustomSelect
                onChange={(value, option) => {
                  setSearchState({
                    ...searchState,
                    status: parseInt(Object.values(option)[0]),
                  });
                }}
                value={searchState?.status ? statusesState?.find((el) => el.id === searchState.status)?.name : 'Все'}
                showSearch
              >
                <Option key={undefined} value={undefined}>
                  Все
                </Option>
                {statusesState?.map((el) => {
                  return (
                    <Option key={el.id} value={el.name}>
                      {el.name}
                    </Option>
                  );
                })}
              </CustomSelect>
            </Col>

            <Col className="right-side-block" span={12}>
              <CustomButton onClick={() => setSearchStatus('search')}>Найти</CustomButton>
            </Col>
            <Col className="right-side-block" span={12}>
              <CustomButton
                onClick={() => {
                  setSearchState({});
                  setSearchStatus('clear');
                }}
                lined
              >
                Очистить
              </CustomButton>
            </Col>
          </Row>
        </FormContainer>
      </FormBlock>

      <CustomTableContainer>
        <Table
          columns={RepresentativeTableColumns}
          dataSource={representativesState}
          pagination={{
            total: paginationState?.count,
            pageSize: paginationState?.perPage,
            onChange: (value, page) => setPaginationState({ ...paginationState, pagination: value, perPage: page }),
          }}
          className={'table'}
        ></Table>
      </CustomTableContainer>
    </PagesContainerUI>
  );
};
